// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import {
  UserService,
  UserServiceRequest,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { NotificationBadge } from '../../../components/NotificationBadge';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useOperatorConfiguration from '../../../hooks/queries/collateral/configuration/useOperatorConfiguration';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import useUserServiceRequests from '../../../hooks/queries/collateral/onboarding/useUserServiceRequests';
import useUserServices from '../../../hooks/queries/collateral/onboarding/useUserServices';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import { validateFulfillsAllRequirements } from '../../../utils/credential';
import {
  ERR_COL_ACC_PROVIDER_SVC,
  ERR_COL_ACC_USER_SVC,
  ERR_COL_CAN_USER_SVC,
  ERR_COL_REJ_USER_SVC,
  ERR_COL_TER_USER_SVC,
  SUC_COL_ACC_USER_SVC,
  SUC_COL_CAN_USER_SVC,
  SUC_COL_REJ_USER_SVC,
  SUC_COL_TER_USER_SVC,
} from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { RequestUserServiceDialog } from './RequestUserServiceDialog';

type Row = {
  contractId: string;
  serviceType: string;
  operator: string;
  user: string;
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'serviceType',
      headerName: 'Service Type',
      valueGetter: (params) => params.row.serviceType,
      flex: 150,
      filterable: false,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      valueGetter: (params) => <PartyDisplay party={params.row.operator} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'user',
      headerName: 'User',
      valueGetter: (params) => <PartyDisplay party={params.row.user} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [userIsOpen, setUserIsOpen] = useState<boolean>(false);
  const party = useModuleParty();
  const userService = useCollateralUserService();
  const userServiceRequests = useUserServiceRequests();
  const { showBoundary } = useErrorBoundary<Error>();

  const isLoading = userService.isLoading || userServiceRequests.isLoading;
  if (isLoading) return null;

  if (!userServiceRequests.data) {
    showBoundary(
      new Error('Failed to load toolbar.', {
        cause: 'User service requests are not available.',
      }),
    );
    return null;
  }
  const userServiceRequest = userServiceRequests.data.find((r) => r.payload.user === party);

  const canRequestUser = !userService.data && !userServiceRequest;

  return (
    <GridToolbarContainer>
      {!!userIsOpen && (
        <RequestUserServiceDialog open={!!userIsOpen} onClose={() => setUserIsOpen(false)} />
      )}

      <SyncButton onClick={() => setUserIsOpen(true)} disabled={!canRequestUser}>
        Request Collateral service
      </SyncButton>
    </GridToolbarContainer>
  );
};

export const Onboarding = () => {
  const credentials = useCredentials();
  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const party = useModuleParty();
  const userServices = useUserServices();
  const userServiceRequests = useUserServiceRequests();
  const { submit: terminateUserService } = useExerciseSubmission(
    UserService.UserService_Terminate,
    SUC_COL_TER_USER_SVC,
    ERR_COL_TER_USER_SVC,
  );

  const { submit: acceptUserServiceRequest } = useExerciseSubmission(
    UserServiceRequest.UserServiceRequest_Accept,
    SUC_COL_ACC_USER_SVC,
    ERR_COL_ACC_USER_SVC,
  );

  const { submit: rejectUserServiceRequest } = useExerciseSubmission(
    UserServiceRequest.UserServiceRequest_Reject,
    SUC_COL_REJ_USER_SVC,
    ERR_COL_REJ_USER_SVC,
  );

  const { submit: cancelUserServiceRequest } = useExerciseSubmission(
    UserServiceRequest.UserServiceRequest_Cancel,
    SUC_COL_CAN_USER_SVC,
    ERR_COL_CAN_USER_SVC,
  );

  const operatorConfiguration = useOperatorConfiguration();
  const { enqueueSnackbar } = useSnackbar();
  const { showBoundary } = useErrorBoundary<Error>();

  const terminateUser = useCallback(
    (r: Row) => {
      return terminateUserService({
        cid: r.contractId as ContractId<UserService>,
        argument: {
          actor: party,
        },
      });
    },
    [terminateUserService, party],
  );

  const acceptUser = useCallback(
    (r: Row) => {
      // Check if data is loaded

      if (!credentials.data) {
        showBoundary(new Error(ERR_COL_ACC_USER_SVC, { cause: 'Credentials are not available.' }));
        return Promise.resolve();
      }
      if (!operatorConfiguration.data) {
        showBoundary(
          new Error(ERR_COL_ACC_PROVIDER_SVC, { cause: `No operator configuration available.` }),
        );
        return Promise.resolve();
      }
      // Find matching credentials
      const { userRequirements } = operatorConfiguration.data.payload;
      const now = new Date(); // local time
      const utcNow = new Date(now.toUTCString()); // utc time
      const { fulfillsAllRequirements, fulfillingCredentials } = validateFulfillsAllRequirements(
        utcNow,
        r.user,
        userRequirements,
        credentials.data,
      );

      if (!fulfillsAllRequirements) {
        enqueueSnackbar(`${ERR_COL_ACC_USER_SVC}: No matching credentials could be found.`, {
          variant: 'error',
        });
        return Promise.resolve();
      }

      // Accept the request
      const payload = {
        operatorConfigurationCid: operatorConfiguration.data.contractId,
        credentialCids: fulfillingCredentials.map((c) => c.contractId),
      };

      return acceptUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: payload,
      });
    },
    [
      acceptUserServiceRequest,
      credentials,
      showBoundary,
      enqueueSnackbar,
      operatorConfiguration.data,
    ],
  );

  const rejectUser = useCallback(
    (r: Row, reason: string) => {
      return rejectUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: { reason },
      });
    },
    [rejectUserServiceRequest],
  );

  const cancelUser = useCallback(
    (r: Row) => {
      return cancelUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: {},
      });
    },
    [cancelUserServiceRequest],
  );

  const createServiceActions = (r: Row) => {
    if (r.serviceType === 'UserService' && r.operator === party)
      return [{ name: 'Terminate', action: terminateUser }];
    return [];
  };

  const [value, setValue] = useState('0');
  const onChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const createRequestActions = (r: Row) => {
    if (r.user !== party) {
      if (r.serviceType === 'UserService')
        return [
          { name: 'Accept', action: acceptUser },
          {
            name: 'Reject',
            action: () => {
              setOnReject(() => (reason: string) => rejectUser(r, reason));
              return Promise.resolve();
            },
          },
        ];
      return [];
    }
    if (r.user === party) {
      if (r.serviceType === 'UserService') return [{ name: 'Cancel', action: cancelUser }];
      return [];
    }
    return [];
  };

  const isLoading =
    userServices.isLoading ||
    userServiceRequests.isLoading ||
    !userServices.data ||
    !userServiceRequests.data;
  if (isLoading) return <Loading />;

  const serviceRows = userServices.data.map((r) => ({
    contractId: r.contractId as string,
    serviceType: 'UserService',
    operator: r.payload.operator,
    user: r.payload.user,
  }));

  const requestRows = userServiceRequests.data.map((r) => ({
    contractId: r.contractId as string,
    serviceType: 'UserService',
    operator: r.payload.operator,
    user: r.payload.user,
  }));
  return (
    <PageWrapper pageName="Onboarding">
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}
      <Tabs value={value} onChange={onChange}>
        <Tab
          value="0"
          label={
            <Typography variant="body2" sx={{ textTransform: 'capitalize', mr: 1 }}>
              Requests
            </Typography>
          }
        />
        <Tab
          value="1"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize', mr: 1 }}>
                Requests
              </Typography>
              <NotificationBadge value={requestRows.length || 0} />
            </Box>
          }
        />
      </Tabs>
      <Divider sx={{ mb: 1 }} />
      {value === '0' && (
        <DataTable
          columns={createHeaders(createServiceActions)}
          rows={serviceRows}
          rowKey={(a) => a.contractId}
          toolbar={Toolbar}
        />
      )}
      {value === '1' && (
        <DataTable
          columns={createHeaders(createRequestActions)}
          rows={requestRows}
          rowKey={(a) => a.contractId}
        />
      )}
    </PageWrapper>
  );
};
